import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Eye, EyeOff, KeyRound } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { api } from "@/lib/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [form, setForm] = useState({ new_password: "", confirm_password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    if (!token) return setError("Token reset tidak ditemukan pada tautan ini.");
    if (form.new_password.length < 8) return setError("Password minimal 8 karakter.");
    if (!/[a-z]/.test(form.new_password) || !/[A-Z]/.test(form.new_password) || !/[0-9]/.test(form.new_password)) {
      return setError("Password harus memiliki huruf besar, huruf kecil, dan angka.");
    }
    if (form.new_password !== form.confirm_password) return setError("Konfirmasi password tidak sama.");

    setLoading(true);
    try {
      const response = await api.post("/auth/reset-password", { token, ...form });
      setMessage(response.data?.message || "Password berhasil diubah.");
      setForm({ new_password: "", confirm_password: "" });
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Tautan reset tidak valid atau sudah kedaluwarsa.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-slate-100">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(34,211,238,.18),transparent_30%),radial-gradient(circle_at_80%_80%,rgba(99,102,241,.22),transparent_34%)]" />
      <div className="relative mx-auto flex min-h-screen max-w-7xl items-center justify-center p-5 sm:p-8">
        <div className="w-full max-w-md rounded-[2rem] border border-white/10 bg-white p-6 text-slate-950 shadow-2xl shadow-black/30 sm:p-9">
          <div className="flex items-center justify-between">
            <Link to="/"><BrandMark accentClassName="text-blue-600" /></Link>
            <Link to="/login" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-950"><ArrowLeft className="h-3.5 w-3.5" /> Kembali login</Link>
          </div>
          <div className="mt-8 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><KeyRound className="h-6 w-6" /></div>
          <h1 className="mt-5 font-display text-4xl font-black tracking-[-0.05em]">Buat password baru.</h1>
          <p className="mt-2 text-sm leading-6 text-slate-500">Gunakan minimal 8 karakter yang memiliki huruf besar, huruf kecil, dan angka.</p>

          {message && (
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-800">
              <div className="flex gap-2"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /><span>{message}</span></div>
              <Link to="/login" className="mt-3 inline-flex rounded-xl bg-emerald-700 px-3 py-2 text-xs font-bold text-white">Login Sekarang</Link>
            </div>
          )}
          {error && <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm leading-6 text-rose-800">{error}</div>}

          {!message && (
            <form onSubmit={submit} className="mt-7 space-y-5">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Password baru</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} required autoComplete="new-password" value={form.new_password} onChange={(event) => setForm({ ...form, new_password: event.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 pr-12 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" />
                  <button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-400">{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
                </div>
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Konfirmasi password</label>
                <input type={showPassword ? "text" : "password"} required autoComplete="new-password" value={form.confirm_password} onChange={(event) => setForm({ ...form, confirm_password: event.target.value })} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" />
              </div>
              <button type="submit" disabled={loading || !token} className="flex w-full items-center justify-center rounded-2xl bg-slate-950 py-3.5 text-sm font-bold text-white transition hover:bg-blue-600 disabled:opacity-50">{loading ? "Menyimpan…" : "Simpan Password Baru"}</button>
            </form>
          )}
          <div className="mt-6 text-center text-[11px] uppercase tracking-[0.16em] text-slate-400">Powered By AntSoft ID</div>
        </div>
      </div>
    </div>
  );
}
